import React, { useEffect, useState } from 'react';
import userService from '../../../../../services/user.service';
import { useParams } from 'react-router-dom';
import { useEyeconxStore } from '../../../../../store/eyeconxStore';
import { Button, Collapse, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Loading from '../../../../common/Loading';
const ActionTooltipButton = ({ tooltip, tooltipId, children, ...buttonProps }) => (
    <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
        <Button {...buttonProps}>{children}</Button>
    </OverlayTrigger>
);

const ROLE_ORDER = ['ADMIN', 'MANAGER', 'ECP', 'STAFF', 'OTHER'];

const getRoleDisplayName = (role) => {
    switch (role) {
        case 'ADMIN':
            return 'Administrators';
        case 'MANAGER':
            return 'Managers';
        case 'ECP':
            return 'Eye Care Professionals';
        case 'STAFF':
            return 'Staff Members';
        case 'OTHER':
            return 'Other Roles';
        default:
            return `${role}s`;
    }
};

const UsersTab = ({ setActiveTab, activeTab }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const { clinicId } = useParams();
    const [userList, setUserList] = useState([]);
    const [groupedUsers, setGroupedUsers] = useState({});

    const setSelectedU = useEyeconxStore((state) => state.setSelectedUser);

    const [expandedRoles, setExpandedRoles] = useState({
        ADMIN: true,
        MANAGER: true,
        ECP: true,
        STAFF: true,
        OTHER: true,
    });

    useEffect(() => {
        if (activeTab && activeTab !== 'clinic-users-tab') return;

        setIsLoading(true);

        userService
            .getUsers(clinicId)
            .then((res) => {
                setIsError(false);
                setErrorMessage('');
                setUserList(res.data);

                const grouped = res.data.reduce((acc, user) => {
                    if (!acc[user.role]) acc[user.role] = [];
                    acc[user.role].push(user);
                    return acc;
                }, {});

                const sortedGrouped = {};
                ROLE_ORDER.forEach((role) => {
                    if (grouped[role]) sortedGrouped[role] = grouped[role];
                });
                Object.keys(grouped).forEach((role) => {
                    if (!ROLE_ORDER.includes(role)) sortedGrouped[role] = grouped[role];
                });

                setGroupedUsers(sortedGrouped);
            })
            .catch((err) => {
                setIsError(true);
                setErrorMessage(err.response?.data?.message || 'Failed to load users');
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [clinicId, activeTab]);

    const handleReminderClick = (user) => {
        setSelectedU(user);
        setActiveTab('clinic-user-reminder');
    };

    const handleSettingsClick = (user) => {
        setSelectedU(user);
        setActiveTab('clinic-user-settings');
    };

    const handleAddClick = () => {
        setActiveTab('clinic-user-add');
    };

    const toggleRoleSection = (role) => {
        setExpandedRoles((prev) => ({ ...prev, [role]: !prev[role] }));
    };

    const renderUserBadges = (user) => (
        <div className="d-flex flex-wrap gap-1 mt-1">
            <span className={`clinic-users-status-badge ${user.isActive ? 'clinic-users-status-badge--active' : 'clinic-users-status-badge--inactive'}`}>
                {user.isActive ? 'Active' : 'Inactive'}
            </span>
            {user.isBlocked && (
                <span className="clinic-users-status-badge clinic-users-status-badge--blocked">
                    <i className="bi bi-lock-fill"></i> Blocked
                </span>
            )}
        </div>
    );

    return (
        <>
            <div className="d-flex align-items-end justify-content-between gap-2 mb-3">
                <div>
                    <h5 className="clinic-users__title mb-1">
                        <i className="bi bi-people-fill text-primary me-1"></i>
                        Clinic Users Management
                    </h5>
                    <p className="text-muted small mb-0">Manage doctors, managers, and staff members for this clinic.</p>
                </div>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleAddClick}>
                    <i className="bi bi-person-plus me-1"></i>
                    Add User
                </button>
            </div>

            <div className="notifications-panel">
                <div className="notifications-panel__header">
                    <span className="d-flex align-items-center gap-2">
                        <i className="bi bi-people"></i>
                        Users by Role
                        {!isLoading && (
                            <span className="text-muted small fw-normal">
                                ({userList.length} user{userList.length !== 1 ? 's' : ''})
                            </span>
                        )}
                    </span>
                </div>

                <div className="notifications-panel__body">
                    {isLoading ? (
                        <Loading />
                    ) : isError ? (
                        <div className="alert alert-danger border-0 mb-0">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            {errorMessage}
                        </div>
                    ) : userList.length > 0 ? (
                        <div>
                            {Object.entries(groupedUsers).map(([role, users]) => (
                                <div className="clinic-users-role-section" key={role}>
                                    <div
                                        className="clinic-users-role-header"
                                        onClick={() => toggleRoleSection(role)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => e.key === 'Enter' && toggleRoleSection(role)}
                                        aria-expanded={expandedRoles[role]}
                                    >
                                        <span className="clinic-users-role-header__title">
                                            {getRoleDisplayName(role)}
                                            <span className="clinic-users-role-count">{users.length}</span>
                                        </span>
                                        <i className={`bi ${expandedRoles[role] ? 'bi-chevron-down' : 'bi-chevron-right'} text-muted`}></i>
                                    </div>

                                    <Collapse in={expandedRoles[role]}>
                                        <div>
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-hover align-middle mb-0 process-queue-table clinic-users-table">
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: '4rem' }}>#</th>
                                                            <th>
                                                                <i className="bi bi-person"></i> Name
                                                            </th>
                                                            <th>
                                                                <i className="bi bi-person-badge"></i> Username
                                                            </th>
                                                            <th>
                                                                <i className="bi bi-shield-check"></i> Auth
                                                            </th>
                                                            <th className="text-end">
                                                                <i className="bi bi-gear"></i> Actions
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {users.map((user) => (
                                                            <tr key={user.id} className="clinic-users-row">
                                                                <td className="text-center text-muted small">{user.number}</td>
                                                                <td>
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <span className="clinic-users-avatar">
                                                                            {user.firstName.charAt(0)}
                                                                            {user.lastName.charAt(0)}
                                                                        </span>
                                                                        <div>
                                                                            <div className="clinic-users-name">
                                                                                {user.firstName} {user.lastName}
                                                                            </div>
                                                                            {renderUserBadges(user)}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <span className="clinic-users-username">{user.username}</span>
                                                                </td>
                                                                <td>
                                                                    {user.isAuthenticated === true ? (
                                                                        <span className="clinic-users-status-badge clinic-users-status-badge--authenticated">
                                                                            <i className="bi bi-check-circle"></i> Yes
                                                                        </span>
                                                                    ) : (
                                                                        <span className="clinic-users-status-badge clinic-users-status-badge--unauthenticated">
                                                                            <i className="bi bi-x-circle"></i> No
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td className="text-end">
                                                                    <div className="d-inline-flex gap-1">
                                                                        {user.role === 'ECP' && (
                                                                            <ActionTooltipButton
                                                                                tooltip="Reminder settings"
                                                                                tooltipId={`reminder-${user.id}`}
                                                                                variant="outline-primary"
                                                                                size="sm"
                                                                                onClick={() => handleReminderClick(user)}
                                                                            >
                                                                                <i className="bi bi-alarm"></i>
                                                                            </ActionTooltipButton>
                                                                        )}
                                                                        <ActionTooltipButton
                                                                            tooltip="Edit user"
                                                                            tooltipId={`edit-${user.id}`}
                                                                            variant="outline-secondary"
                                                                            size="sm"
                                                                            onClick={() => handleSettingsClick(user)}
                                                                        >
                                                                            <i className="bi bi-pencil"></i>
                                                                        </ActionTooltipButton>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </Collapse>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="clinic-users-empty">
                            <i className="bi bi-people"></i>
                            No users found for this clinic.
                            <div className="mt-3">
                                <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleAddClick}>
                                    <i className="bi bi-person-plus me-1"></i>
                                    Add First User
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default UsersTab;
